import { Text } from "@dropbox/dig-components/typography";
import { Link } from "@mirage/link/Link";
import { useMultimediaPreview } from "@mirage/mosaics/SearchPage/Multimedia/hooks/useMultimediaPreview";
import { ResultSubtitle } from "@mirage/search/SearchResults/Metadata/ResultSubtitle";
import ResultIcon from "@mirage/search/SearchResults/ResultIcon";
import { ResultBody } from "@mirage/search/SearchResults/ResultRow/ResultBody";
import { TitleHighlighter } from "@mirage/search/SearchResults/TitleHighlighter";
import {
  getDisplayTypeForLogicalType,
  getResultConfigForDisplayType,
  getTitleConfig,
} from "@mirage/search/SearchResults/util/config";
import { getAttachments } from "@mirage/search/SearchResults/util/resultUtil";
import type { SearchResult } from "@mirage/service-dbx-api";
import { KeyCodes } from "@mirage/shared/util/constants";
import classNames from "classnames";
import styles from "./DebugResult.module.css";

interface DebugResultProps {
  result: SearchResult;
  query: string;
  resultPosition: number;
  onSelect: (resultId: string) => void;
  isSelected: boolean;
}

export const DebugResult = ({
  result,
  query,
  resultPosition,
  onSelect,
  isSelected,
}: DebugResultProps) => {
  // Fetch base64 image for sharing between ResultIcon and ResultAnnotations
  const { base64Image } = useMultimediaPreview(
    result.previewUrl || undefined,
    512,
  );
  const thumbnailUrl = base64Image ? base64Image : undefined;

  // Use the same display logic as production but simplified
  const displayType = getDisplayTypeForLogicalType(result, true);
  const displayConfig = getResultConfigForDisplayType(displayType);
  const titleSegments = displayConfig.title
    .map((titleField) => getTitleConfig(titleField, result))
    .filter((titleSegment) => titleSegment !== null);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === KeyCodes.enter || event.key === KeyCodes.space) {
      event.preventDefault();
      onSelect(result.uuid);
    }
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.selected]: isSelected,
      })}
      onClick={() => onSelect(result.uuid)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
    >
      <div className={styles.topRow}>
        <div className={styles.iconContainer}>
          <ResultIcon
            result={result}
            size="large"
            thumbnailUrl={thumbnailUrl}
            shouldFetchThumbnail={false}
          />
        </div>
        <div className={styles.textContainer}>
          <Link
            href={result.url || ""}
            className={styles.titleContainer}
            variant="monochromatic"
            hasNoUnderline
          >
            {titleSegments.map((titleSegment) => (
              <Text
                key={titleSegment}
                variant="label"
                size="medium"
                className={styles.titleText}
              >
                <TitleHighlighter
                  textSize="medium"
                  query={query}
                  title={titleSegment}
                />
              </Text>
            ))}
          </Link>
          <div>
            <Text size="small" color="subtle" variant="label">
              <ResultSubtitle
                result={result}
                subtitleFields={displayConfig.subtitle}
                onClickPerson={() => {}}
                activeFilters={[]}
                currentUserNames={[]}
                forceUseConfigDisplayType={true}
              />
            </Text>
          </div>
        </div>
      </div>

      <div className={styles.bodyContainer}>
        <ResultBody
          result={result}
          query={query}
          activeFilters={[]}
          bodyFields={displayConfig.body}
          resultPosition={resultPosition}
          annotationsEnabled={true}
          base64Image={thumbnailUrl}
        />
        <Text size="small" color="subtle">
          Relevance Score: {result.relevanceScore}
        </Text>
      </div>

      {/* Render attachments if any */}
      {getAttachments(result).length > 0 && (
        <div className={styles.attachmentsContainer}>
          {getAttachments(result).map((attachment, index) => (
            <div key={index} className={styles.attachment}>
              <Text size="small" color="subtle">
                {attachment.title || attachment.url}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
